/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useContext, useEffect, useRef, useState } from "react"
import { BaseButton, Icon } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H3, P } from "@cd2/text"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useGlobalState } from "./GlobalStateProvider"
import { buttonConfigs } from "../constants/buttonConfigs"
import { icons } from "../constants/icons"
import { brandColors } from "./EOSThemeProvider"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { IRecord } from "../types/schema"
import { useManagedDialog } from "../hooks/useManagedDialog"
import { applyTooltipChange, canEditLinkTooltips } from "../utils/linkTooltips"

// Editable tooltips for sidebar and toolbar links, stored on globalSettings.linkTooltips as a map
// of link key -> text. Links without text show no tooltip.

export const LINK_TOOLTIP_DELAY = 1000

const QUERY_KEY = ["linkTooltips"]
const EDIT_MODE_STORAGE_KEY = "linkTooltipsEditMode"

type ISettings = IRecord<"globalSettings", "linkTooltips">

async function fetchSettings() {
  return window.skeem.fetch2<ISettings>({
    first: { name: "globalSettings", attributes: ["linkTooltips"] },
  })
}

interface IEditing {
  key: string
  label: string
}

type IOpenEditorDialog = ReturnType<typeof useLinkTooltipEditorDialog>

interface ILinkTooltipsContext {
  tooltips: Record<string, string>
  canEdit: boolean
  editMode: boolean
  setEditMode(value: boolean): void
  openEditor(editing: IEditing): void
  save(key: string, content: string): Promise<void>
  registerEditorDialog(open: IOpenEditorDialog | null): void
}

const LinkTooltipsContext = React.createContext<ILinkTooltipsContext | null>(null)

export function LinkTooltipsProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useGlobalState()
  const alerts = useSiteAlerts()
  const queryClient = useQueryClient()
  const canEdit = canEditLinkTooltips(currentUser)

  const [editMode, setEditModeState] = useState(() => {
    try {
      return localStorage.getItem(EDIT_MODE_STORAGE_KEY) === "true"
    } catch {
      return false
    }
  })
  // the managed dialog has to be created below ManagedModalProvider, so LinkTooltipEditorHost
  // registers it here
  const editorDialogRef = useRef<IOpenEditorDialog | null>(null)

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSettings,
    enabled: !!currentUser?.id,
    staleTime: 1000 * 60 * 5,
  })
  const tooltips = data?.linkTooltips ?? {}

  function setEditMode(value: boolean) {
    setEditModeState(value)
    try {
      localStorage.setItem(EDIT_MODE_STORAGE_KEY, String(value))
    } catch {}
  }

  async function save(key: string, content: string) {
    // re-fetch before writing so we don't overwrite tooltips someone else has saved meanwhile
    const latest = await fetchSettings()
    if (!latest) {
      return
    }
    const next = applyTooltipChange(latest.linkTooltips, key, content)
    await window.skeem.mutate({ globalSettings: { update: { id: latest.id, linkTooltips: next } } })
    queryClient.setQueryData(QUERY_KEY, { ...latest, linkTooltips: next })
    alerts.genericSuccess(content.trim() ? "Tooltip saved" : "Tooltip removed")
  }

  async function openEditor({ key, label }: IEditing) {
    if (!editorDialogRef.current) {
      return
    }
    const content = await editorDialogRef.current({
      label,
      initialContent: tooltips[key] ?? "",
    })
    if (content !== null) {
      await save(key, content)
    }
  }

  return (
    <LinkTooltipsContext.Provider
      value={{
        tooltips,
        canEdit,
        editMode: canEdit && editMode,
        setEditMode,
        openEditor,
        save,
        registerEditorDialog: open => {
          editorDialogRef.current = open
        },
      }}
    >
      {children}
    </LinkTooltipsContext.Provider>
  )
}

export function useLinkTooltips() {
  return useContext(LinkTooltipsContext)
}

// Resolves with the new tooltip text ("" to remove it), or null when cancelled
function useLinkTooltipEditorDialog() {
  return useManagedDialog<string | null, { label: string; initialContent: string }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(null))
      const [content, setContent] = useState(dialogData.initialContent)

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div>
            <H3>Tooltip for "{dialogData.label}"</H3>
            <P blockSpacing={0} size="small">
              Shown when hovering over this link for 1 second. Leave empty to show no tooltip.
            </P>
          </div>
          <Fields.TextArea value={content} onChange={setContent} placeholder="Tooltip text..." />
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              gap: 10px;
            `}
          >
            {dialogData.initialContent && (
              <BaseButton {...buttonConfigs.cancelButton} onClick={() => onResolve("")}>
                Remove tooltip
              </BaseButton>
            )}
            <BaseButton {...buttonConfigs.cancelButton} onClick={() => onResolve(null)}>
              Cancel
            </BaseButton>
            <BaseButton {...buttonConfigs.generalNight} onClick={() => onResolve(content)}>
              Save
            </BaseButton>
          </div>
        </div>
      )
    },
  })
}

// Render after <ManagedModalProvider /> so the dialog's modal can be created on mount
export function LinkTooltipEditorHost() {
  const context = useLinkTooltips()
  const openDialog = useLinkTooltipEditorDialog()
  const registerEditorDialog = context?.registerEditorDialog

  useEffect(() => {
    registerEditorDialog?.(openDialog)
    return () => registerEditorDialog?.(null)
  })

  return null
}

// Small pencil shown over a link while in edit mode. Clicking it opens the editor rather than
// following the link.
export function LinkTooltipEditButton({
  tooltipKey,
  label,
  hasContent,
}: {
  tooltipKey: string
  label: string
  hasContent: boolean
}) {
  const context = useLinkTooltips()
  if (!context?.editMode) {
    return null
  }
  return (
    <button
      type="button"
      title={hasContent ? "Edit tooltip" : "Add tooltip"}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        context.openEditor({ key: tooltipKey, label })
      }}
      css={css`
        position: absolute;
        top: 2px;
        right: 2px;
        z-index: 2;
        width: 18px;
        height: 18px;
        padding: 0;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        font-size: 9px;
        line-height: 18px;
        text-align: center;
        color: ${hasContent ? "white" : brandColors.night};
        background-color: ${hasContent ? brandColors.night : brandColors.mintPop};
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      `}
    >
      <Icon icon={icons.edit} />
    </button>
  )
}

// Banner shown while editing tooltips so it's obvious how to get out of edit mode
export function LinkTooltipsEditModeBanner() {
  const context = useLinkTooltips()
  if (!context?.editMode) {
    return null
  }
  return (
    <div
      css={css`
        position: fixed;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 8px 8px 8px 16px;
        border-radius: 4px;
        background-color: ${brandColors.night};
        color: white;
        font-size: 13px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      `}
    >
      <span>Editing link tooltips - click the pencil on any sidebar or toolbar link</span>
      <BaseButton {...buttonConfigs.generalNight} onClick={() => context.setEditMode(false)}>
        Done
      </BaseButton>
    </div>
  )
}
